import type { ArchiveJob, ArchiveStore, MediaApi, MediaPurpose, MediaUploadStatus, MediaView } from "./mediaTypes";
import type { MasterImage } from "./imageCompression";
import { savedMediaNotice, servableCopyCount } from "./mediaPresets";
import { registerMedia } from "./mediaReferences";

type Dependencies = {
  api: MediaApi;
  store: ArchiveStore;
  ownerKey: () => string;
  compress: (original: Blob) => Promise<MasterImage>;
  hash: (original: Blob) => Promise<string>;
  put: (url: string, blob: Blob, type: string | null, progress: (percent: number) => void,
    signal?: AbortSignal) => Promise<void>;
};
type Listener = (mediaId: string, status: MediaUploadStatus, progress: number, message?: string) => void;

function sourceContentType(file: Blob): string {
  if (file.type && file.type !== "application/octet-stream") return file.type;
  if (typeof File !== "undefined" && file instanceof File) {
    if (/\.heic$/i.test(file.name)) return "image/heic";
    if (/\.heif$/i.test(file.name)) return "image/heif";
    if (/\.avif$/i.test(file.name)) return "image/avif";
  }
  return file.type;
}

/** Return operational readiness first; original archival is a separate resumable task. */
export class MediaUploadManager {
  private jobs = new Map<string, ArchiveJob>();
  private active = new Map<string, AbortController>();
  private archiveTail: Promise<unknown> = Promise.resolve();
  private listeners = new Set<Listener>();
  constructor(private readonly deps: Dependencies) {}
  subscribe(listener: Listener) { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }
  private emit(id: string, status: MediaUploadStatus, progress = 0, message?: string) {
    this.listeners.forEach(listener => listener(id, status, progress, message));
  }
  private assertOwner(key: string) {
    if (!key || this.deps.ownerKey() !== key) throw new Error("Upload session changed; sign in to resume");
  }
  private async save(job: ArchiveJob) {
    this.jobs.set(job.mediaId, job);
    try { await this.deps.store.put(job); }
    catch {
      // Quota may reject the large Blob while still allowing its recovery fingerprint.
      try { await this.deps.store.put({ ...job, original: undefined }); } catch { /* Memory remains available while HKP stays open. */ }
      this.emit(job.mediaId, job.status, 0, "Keep HKP open; reselect the original if you leave before archival finishes");
    }
  }

  async upload(original: File, purpose: MediaPurpose,
    preview?: (url: string) => void): Promise<{ mediaId: string; media: MediaView; archive: Promise<MediaView | undefined>; disposePreview: () => void }> {
    const owner = this.deps.ownerKey();
    this.assertOwner(owner);
    if (original.size < 1 || original.size > 100 * 1024 * 1024) throw new Error("Choose a photo under 100 MiB");
    const previewUrl = URL.createObjectURL(original);
    preview?.(previewUrl);
    const disposePreview = () => URL.revokeObjectURL(previewUrl);
    let id = "";
    try {
      this.emit(id, "OPTIMIZING_MASTER");
      const [master, sha256] = await Promise.all([this.deps.compress(original), this.deps.hash(original)]);
      this.assertOwner(owner);
      const initialized = await this.deps.api.init({ purpose, originalFileName: original.name,
        originalContentType: sourceContentType(original), originalBytes: original.size, originalSha256: sha256,
        masterContentType: master.blob.type, masterBytes: master.blob.size,
        masterWidth: master.width, masterHeight: master.height });
      id = initialized.mediaId;
      this.assertOwner(owner);
      this.emit(id, "MASTER_UPLOADING");
      await this.deps.put(initialized.masterUpload.url, master.blob, initialized.masterUpload.contentType,
        percent => this.emit(id, "MASTER_UPLOADING", percent));
      this.assertOwner(owner);
      const media = await this.deps.api.completeMaster(id);
      this.assertOwner(owner);
      if (!media.operationalReady) throw new Error("Master verification is incomplete");
      const job: ArchiveJob = { mediaId: id, ownerKey: owner, fileName: original.name,
        contentType: sourceContentType(original), bytes: original.size, sha256, original, status: "OPERATIONAL_READY" };
      await this.save(job);
      this.assertOwner(owner);
      this.emit(id, "OPERATIONAL_READY", 100);
      const archive = this.resume(id);
      // Consumers may observe archive failure separately; do not create unhandled promise rejections.
      void archive.catch(() => undefined);
      return { mediaId: id, media, archive, disposePreview };
    } catch (error) { disposePreview(); throw error; }
  }

  /** Resolves with the delivery view once the original is archived (copies included when they exist by then). */
  resume(mediaId: string, selectedAgain?: File): Promise<MediaView | undefined> {
    if (this.active.has(mediaId)) return Promise.reject(new Error("Original upload is already in progress"));
    const controller = new AbortController();
    this.active.set(mediaId, controller);
    const task = this.archiveTail.catch(() => undefined)
      .then(() => this.performResume(mediaId, selectedAgain, controller))
      .finally(() => this.active.delete(mediaId));
    this.archiveTail = task;
    return task;
  }

  private async performResume(mediaId: string, selectedAgain: File | undefined,
    controller: AbortController): Promise<MediaView | undefined> {
    const job = this.jobs.get(mediaId) || await this.deps.store.get(mediaId);
    if (!job) throw new Error("Original upload record is unavailable");
    this.assertOwner(job.ownerKey);
    if (controller.signal.aborted) throw new Error("Upload interrupted");
    const original = selectedAgain || job.original;
    if (!original) throw new Error("Please select the same original photograph again");
    if (original.size !== job.bytes || sourceContentType(original) !== job.contentType
      || (selectedAgain && (selectedAgain.name !== job.fileName || await this.deps.hash(selectedAgain) !== job.sha256)))
      throw new Error("This is a different source photograph");
    this.assertOwner(job.ownerKey);
    if (controller.signal.aborted) throw new Error("Upload interrupted");
    try {
      await this.save({ ...job, original, status: "ORIGINAL_UPLOADING" });
      const session = await this.deps.api.startOriginal(mediaId);
      let copies = 0;
      let finalView: MediaView | undefined;
      if (session.archiveStatus !== "ORIGINAL_ARCHIVED") {
        if (!Number.isInteger(session.partBytes) || session.partBytes < 1
          || session.partCount !== Math.ceil(original.size / session.partBytes))
          throw new Error("Invalid archival upload session");
        const completed = new Map(session.uploadedParts.map(part => [part.partNumber, part]));
        for (let part = 1; part <= session.partCount; part++) {
          this.assertOwner(job.ownerKey);
          if (controller.signal.aborted) throw new Error("Upload interrupted");
          const start = (part - 1) * session.partBytes;
          const chunk = original.slice(start, Math.min(original.size, start + session.partBytes));
          if (completed.get(part)?.bytes === chunk.size) continue;
          let uploaded = false;
          for (let attempt = 0; attempt < 3 && !uploaded; attempt++) {
            try {
              this.assertOwner(job.ownerKey);
              const signed = await this.deps.api.presignPart(mediaId, part);
              this.assertOwner(job.ownerKey);
              await this.deps.put(signed.url, chunk, null, percent => {
                this.emit(mediaId, "ORIGINAL_UPLOADING",
                  Math.round(100 * (start + chunk.size * percent / 100) / original.size));
              }, controller.signal);
              uploaded = true;
            } catch (error) {
              if (attempt === 2 || controller.signal.aborted) throw error;
            }
          }
        }
        this.assertOwner(job.ownerKey);
        const completedView = await this.deps.api.completeOriginal(mediaId);
        if (completedView.archiveStatus !== "ORIGINAL_ARCHIVED") throw new Error("Original verification is incomplete");
        copies = servableCopyCount(completedView);
        finalView = completedView;
      } else {
        try { finalView = await this.deps.api.get(mediaId); copies = servableCopyCount(finalView); } catch { copies = 0; }
      }
      this.assertOwner(job.ownerKey);
      // Every caller's HKPImage resolves URLs through mediaReferences; give it the copies, not the master-only view.
      if (finalView && copies > 0) registerMedia(finalView);
      this.jobs.delete(mediaId);
      await this.deps.store.remove(mediaId).catch(() => undefined);
      this.emit(mediaId, "ORIGINAL_ARCHIVED", 100, savedMediaNotice(copies));
      return finalView;
    } catch (error) {
      if (this.deps.ownerKey() === job.ownerKey)
        await this.save({ ...job, original, status: "ORIGINAL_FAILED_RETRYABLE" });
      if (this.deps.ownerKey() === job.ownerKey) this.emit(mediaId, "ORIGINAL_FAILED_RETRYABLE", 0,
        error instanceof Error ? error.message : "Original archival interrupted");
      throw error;
    }
  }

  async restore(): Promise<ArchiveJob[]> {
    const owner = this.deps.ownerKey();
    if (!owner) return [];
    const records = await this.deps.store.list();
    const current = records.filter(job => job.ownerKey === owner);
    current.forEach(job => this.jobs.set(job.mediaId, job));
    return current;
  }

  interruptAll() { this.active.forEach(controller => controller.abort()); }
}
